import { User } from '@nextforce/api'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { TaskUserListScreenProps } from 'home/stack/types'
import React, { useCallback, useLayoutEffect, useMemo } from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { useRecentStoresContext } from 'stores/context'
import { useAddEditTaskContext } from 'tasks/context'
import { useTask } from 'tasks/queries/detail'
import { useReassignTask } from 'tasks/queries/reassign'
import { useUserList } from 'users/queries/list'
import { TaskUserListView } from './views.tsx'

export function TaskUserListScreen({
  navigation,
  route,
}: TaskUserListScreenProps) {
  const { taskId, fromDate, toDate, type } = route.params
  const { handleNavigation } = useRoutingContext()
  const reassignTaskMutation = useReassignTask()
  const { activeStoreId } = useRecentStoresContext()
  const { setTaskFields } = useAddEditTaskContext()

  const taskQuery = useTask(
    taskId,
    { fromDate, toDate },
    type === 'reassign' && !!taskId
  )

  const userListQuery = useUserList({}, activeStoreId, true)

  const filteredUsers = useMemo(() => {
    if (type === 'assign') {
      return userListQuery.data?.users
    }
    const existingAssignee = taskQuery.data?.assignedTo.id
    return userListQuery.data?.users?.filter(
      user => user.id !== existingAssignee
    )
  }, [type, taskQuery.data?.assignedTo.id, userListQuery.data?.users])

  useLayoutEffect(() => {
    showHeaderTitle(
      navigation,
      type === 'reassign' ? t('reassignTask') : t('assignTask')
    )
  }, [navigation, type])

  const onPerformAssign = useCallback(
    (user: User) => {
      setTaskFields(prev => ({
        ...prev,
        assignedTo: user.id.toString(),
      }))
      navigation.goBack()
    },
    [navigation, setTaskFields]
  )

  const onPerformReassign = useCallback(
    async (user: User) => {
      if (!user || !user.id) return
      if (!taskId) {
        return
      }
      try {
        await reassignTaskMutation.mutateAsync({
          id: taskId,
          assignedTo: user.id.toString(),
        })
        navigation.goBack()
      } catch (e) {
        errorAlert(e, t('errorReassigningTask'))
      }
    },
    [taskId, reassignTaskMutation, navigation]
  )

  return (
    <StatusBoundary
      isLoading={userListQuery.isLoading}
      error={userListQuery.error}
    >
      <TaskUserListView
        type={type}
        users={filteredUsers}
        noUsersNote={
          type === 'reassign'
            ? t('noUsersReassignTaskNote')
            : t('noUsersAssignTaskNote')
        }
        onPerformReassign={onPerformReassign}
        onPerformAssign={onPerformAssign}
      />
    </StatusBoundary>
  )
}
